import { ReactNode } from 'react'
import cn from 'clsx'
import { NavLink } from 'react-router-dom'
import { Badge, Space, Drawer, Tooltip } from 'antd'

import styles from './Header.module.scss'
import { logout } from 'modules/auth'
import { BrokerDetails, BorrowerDetails, useGetProfile, ProfileForm, PasswordChangeForm } from 'modules/profile'
import { PermissionType } from 'common/types'
import { useGetNotifications } from 'common/api'
import { useOpen } from 'common/hooks'
import {
  Text,
  Box,
  Modal,
  Button,
  UserAvatar,
  DotsMenu,
  OrganisationsIcon,
  CertificateMedalIcon,
  InvoiceAccountingIcon,
  RequestIcon,
  FolderBookmarkIcon,
  ProfileCircleIcon,
  ReconciliationIcon,
} from 'common/components'
import { IconComponent } from 'common/types'
import { ROLE_NAME } from 'modules/users'

interface LinkInterface {
  label: string
  Icon: IconComponent
  to: string
  whiteList?: string[]
  blackList?: string[]
  permissionRequired?: PermissionType
}

interface LinkProps {
  children: ReactNode
  label: string
  to: string
}
const links: LinkInterface[] = [
  {label: 'Projects',       Icon: FolderBookmarkIcon,     to: '/projects'},
  {label: 'Requests',       Icon: RequestIcon,            to: '/requests'},
  {label: 'Dashboard',      Icon: InvoiceAccountingIcon,  to: '/cashflow', blackList: ['associated-manager']},
  {label: 'Users',          Icon: ProfileCircleIcon,      to: '/users'},
  {label: 'RBAC',           Icon: CertificateMedalIcon,   to: '/rbac', permissionRequired: 'view rbac'},
  {label: 'Orgs',           Icon: OrganisationsIcon,      to: '/orgs'},
  {label: 'Reconciliation', Icon: ReconciliationIcon,     to: '/reconciliations', permissionRequired: 'view reconciliation'},
]

const DevelopmentLogout = () => {
  const handleLogout = () => logout()

  return (
    <Button
      className={styles.logout}
      variant="secondary"
      onClick={handleLogout}
    >
      Logout
    </Button>
  )
}

const Link = ({ label, to, children }: LinkProps) => (
  <NavLink
    className={({ isActive }) => cn(styles.link, isActive && styles.link_active)}
    to={to}
    aria-label={label}
  >
    {children}
  </NavLink>
)

const SimpleNavigation = () => {
  const { data: profile } = useGetProfile()
  const [isOpen, handleOpen, handleClose] = useOpen()

  return (
    <>
      <Box fadeIn className={cn(styles.navigation, styles.navigation_broker)}>
        <button
          className={cn(
            styles.link,
            styles.link_button,
            isOpen && styles.link_active
          )}
          onClick={handleOpen}
        >
          <Space>
            <UserAvatar size={26} src={profile.photo} alt={profile.firstName} />
            <Text as="span" color="charcoal">
              {profile.firstName}
            </Text>
          </Space>
        </button>
      </Box>
      {profile.role === 'user' ? (
        <Drawer
          width={320}
          closable={false}
          open={isOpen}
          onClose={handleClose}
        >
          <BorrowerDetails />
        </Drawer>
      ) : (
        <Modal
          title="Your personal details"
          open={isOpen}
          onCancel={handleClose}
        >
          <BrokerDetails />
        </Modal>
      )}
    </>
  )
}

const Menu = () => {
  const [isEditDetailsOpen, handleEditDetailsOpen, handleEditDetailsClose] =
    useOpen()
  const [
    isChangePasswordOpen,
    handleChangePasswordOpen,
    handleChangePasswordClose,
  ] = useOpen()

  const handleLogout = () => logout()

  const menuItems = [
    {
      label: 'Edit details',
      onClick: handleEditDetailsOpen,
    },
    {
      label: 'Change password',
      onClick: handleChangePasswordOpen,
    },
    { label: 'Log out', onClick: handleLogout },
  ]

  return (
    <>
      <DotsMenu items={menuItems} />
      <Modal
        title="Edit details"
        open={isEditDetailsOpen}
        onCancel={handleEditDetailsClose}
      >
        <ProfileForm onClose={handleEditDetailsClose} />
      </Modal>
      <Modal
        title="Change password"
        open={isChangePasswordOpen}
        onCancel={handleChangePasswordClose}
      >
        <PasswordChangeForm onClose={handleChangePasswordClose} />
      </Modal>
    </>
  )
}

const Navigation = () => {
  const { data: profile } = useGetProfile()
  const { data: notifications } = useGetNotifications()

  if (!profile) return null

  const permissions = profile.permissions?.map((x: string) => (x.toLocaleLowerCase() as PermissionType)) || []

  return (
    <Box fadeIn as="nav" className={styles.navigation}>
      <ul className={styles.list}>
        {
          links.filter(
            (link) => (
              !!link.permissionRequired ? permissions.includes(link.permissionRequired)
                :
                !!link.whiteList ? link.whiteList.includes(profile.role)
                  :
                  !!link.blackList ? !link.blackList.includes(profile.role)
                    :
                    true
            )
          ).map(({ Icon, ...link }) => (
            <Tooltip key={link.label} title={link.label}>
              <li key={link.label} className={styles.item}>
                <Link to={link.to} label={link.label}>
                  {/* <Icon /> */}
                  <h1>{link.label}</h1>
                </Link>
              </li>
            </Tooltip>
          ))}
      </ul>
      {process.env.NODE_ENV !== 'production' && <DevelopmentLogout />}
      <Space className={styles.profile}>
        <Link to="profile" label="Profile">
          <UserAvatar size={26} src={profile.photo} alt={profile.firstName} />
          <div className={styles.profile_details}>
            <Space>
              <Text as="span" color="black" size="lg" weight="bold">
                {profile.firstName}  {profile.lastName}
              </Text>
              {Boolean(notifications?.length) && (
                <Badge
                  className={styles.badge}
                  size="small"
                  count={notifications.length}
                />
              )}
            </Space>
            <Text as="p" color="grey" size="sm">
              {ROLE_NAME[profile.role]}
            </Text>
          </div>
        </Link>
        <Menu />
      </Space>
    </Box>
  )
}

const Content = () => {
  const { data: profile } = useGetProfile()

  if (!profile) return null

  if (profile.role === 'broker' || profile.role === 'user')
    return <SimpleNavigation />

  return <Navigation />
}

export default function Header() {
  return (
    <header className={styles.root}>
      <Box fadeIn>
        {/* <img width={99} height={36} src={logoImg} alt="VC Online" /> */}
        <h1 style={{ whiteSpace: 'nowrap', fontSize: '38px', fontWeight: '500' }}>VC Online</h1>
      </Box>
      <Content />
    </header>
  )
}
